/**
 * Ресурсный модуль import-wizard (браузер → /api/import-wizard). Слой L2.
 *
 * Чистые типизированные функции поверх http-ядра, без React. Хук
 * (import-wizard.hooks) зовёт этот модуль; компоненты — только хук.
 *
 * NB: реальные роуты мастера импорта — /api/import-wizard/{upload,validate,
 * preview,commit,bulk-upload} (см. server/* и app-обёртки src/app/api/import-wizard/*).
 */

import { http } from '@/lib/api/http'
import { buildModuleBase } from '@/lib/api/module-path'
import type {
  ColumnMatch,
  ValidationSummary,
  DuplicateCandidate,
  ImportPreviewResponse,
} from './import-wizard.types'

const BASE = buildModuleBase('import-wizard')

// ── DTO ресурса (форма провода). Только поля, которые реально читает хук. ──

/** Распарсенный один файл (import или export), как его отдаёт /upload. */
export interface UploadedFileDto {
  headers: string[]
  sampleRows: Record<string, unknown>[]
  totalRows: number
  columnMatches: ColumnMatch[]
}

/** Ответ /api/import-wizard/upload. Поля import/export опциональны (legacy single-file). */
export interface ImportUploadResponse {
  sessionId: string
  import?: UploadedFileDto
  export?: UploadedFileDto
  // Legacy single-file поля верхнего уровня (fallback в хуке).
  headers?: string[]
  sampleRows?: Record<string, unknown>[]
  totalRows?: number
  columnMatches?: ColumnMatch[]
}

export interface ImportValidateRequest {
  sessionId: string
  mappingConfig: Record<string, string>
  importMappingConfig: Record<string, string>
  exportMappingConfig: Record<string, string>
}

export interface ImportValidateResponse {
  validation: ValidationSummary
  duplicates: DuplicateCandidate[]
}

/** Ответ /api/import-wizard/bulk-upload (прямая загрузка одного файла в DWH). */
export interface BulkUploadResponse {
  loadedRows?: number
  totalRows?: number
  errors?: number
}

export const importWizardApi = {
  /**
   * Загрузка файлов импорта/экспорта. multipart/form-data → проксируется на
   * ML-service (Python, до 300MB). http-ядро само определяет FormData и не
   * ставит JSON Content-Type (браузер выставит boundary).
   */
  upload: (formData: FormData, opts?: { signal?: AbortSignal }) =>
    http.post<ImportUploadResponse>(`${BASE}/upload`, formData, { signal: opts?.signal }),

  /** Валидация смапленных данных сессии. */
  validate: (body: ImportValidateRequest, opts?: { signal?: AbortSignal }) =>
    http.post<ImportValidateResponse>(`${BASE}/validate`, body, { signal: opts?.signal }),

  /** Предпросмотр результата импорта. */
  preview: (body: { sessionId: string }, opts?: { signal?: AbortSignal }) =>
    http.post<ImportPreviewResponse>(`${BASE}/preview`, body, { signal: opts?.signal }),

  /** Коммит импорта (с опциональными действиями по дубликатам). */
  commit: (
    body: { sessionId: string; duplicateActions?: Record<number, string> },
    opts?: { signal?: AbortSignal },
  ) => http.post<unknown>(`${BASE}/commit`, body, { signal: opts?.signal }),

  /**
   * Прямая загрузка одного файла (импорт/экспорт/НСИ) в DWH через
   * /api/import-wizard/bulk-upload. multipart/form-data → ML-service (до 300MB),
   * поэтому таймаут расширен. http-ядро не ставит JSON Content-Type для FormData.
   */
  bulkUpload: (formData: FormData, opts?: { signal?: AbortSignal }) =>
    http.post<BulkUploadResponse>(`${BASE}/bulk-upload`, formData, {
      signal: opts?.signal,
      timeoutMs: 600_000,
    }),
}
